/**
 * На что ляжет отделка из панели «Отделка»: по выделению находит стороны стен.
 * Выделена стена — одна её сторона (та, по которой кликнули в 3D, а без неё — смотрящая
 * в комнату); выделена комната — все её стены изнутри. Иначе отделку класть некуда.
 */
import { wallFaceKey, type WallFace, type WallFinish } from "@/shared/plan";
import { useProjectStore } from "@/shared/store";
import { SelectionKind, useEditorStore } from "@/widgets/editor";
import { detectRooms, roomOfFace, roomTitle, roomWallFaces, type Room } from "@/widgets/plan";

/** Сторона выделенной стены и комната, в которую она смотрит (null — наружу). */
export interface FinishTargetSide {
	side: 1 | -1;
	room: Room | null;
	/** «Помещение 2» или «Снаружи» */
	label: string;
}

export interface FinishTarget {
	faces: WallFace[];
	/** Подпись цели: «Стена · Помещение 2», «Все стены · Помещение 2» */
	label: string;
	/** Отделка, общая для всех граней цели; null — разная или нет */
	currentId: string | null;
	/** Общая для всех граней запись отделки вместе с цветом; нет — разная или нет совсем */
	shown: WallFinish | undefined;
	/** Хотя бы на одной грани цели есть отделка */
	anyFinish: boolean;
	/** У стены — обе стороны для переключателя, у комнаты пусто */
	sides: FinishTargetSide[];
	/** Комната выбранной стороны стены — для «На всю комнату» */
	sideRoom: Room | null;
}

export const useFinishTarget = (): FinishTarget | null => {
	const selection = useEditorStore((s) => s.selection);
	const graph = useProjectStore((s) => s.project.layers.walls);
	const finishes = useProjectStore((s) => s.project.layers.finishes.items);
	const roomNames = useProjectStore((s) => s.project.layers.rooms);
	if (!selection) return null;

	const rooms = detectRooms(graph);
	const roomName = (room: Room | null) => {
		const index = room ? rooms.findIndex((item) => item.id === room.id) : -1;
		return room && index >= 0 ? roomTitle(roomNames, room.id, index) : "Снаружи";
	};

	let faces: WallFace[] = [];
	let label = "";
	let sides: FinishTargetSide[] = [];
	let sideRoom: Room | null = null;
	if (selection.kind === SelectionKind.Wall) {
		if (!graph.walls[selection.id]) return null;
		sides = ([1, -1] as const).map((side) => {
			const room = roomOfFace(graph, rooms, { wallId: selection.id, side });
			return { side, room, label: roomName(room) };
		});
		const side = selection.side ?? sides.find((s) => s.room)?.side ?? 1;
		sideRoom = sides.find((s) => s.side === side)?.room ?? null;
		faces = [{ wallId: selection.id, side }];
		label = `Стена · ${roomName(sideRoom)}`;
	} else if (selection.kind === SelectionKind.Room) {
		const room = rooms.find((r) => r.id === selection.id);
		if (!room) return null;
		faces = roomWallFaces(graph, room);
		label = `Все стены · ${roomName(room)}`;
	}
	if (faces.length === 0) return null;

	const current = faces.map((face) => finishes[wallFaceKey(face.wallId, face.side)]);
	const first = current[0];
	// Отделка общая, только если на всех гранях одна и та же и одного цвета
	const uniform =
		first !== undefined &&
		current.every((f) => f?.finish === first.finish && f?.color === first.color);
	const ids = current.map((f) => f?.finish ?? null);
	const currentId = ids.every((id) => id === ids[0]) ? ids[0] : null;
	return {
		faces,
		label,
		currentId,
		shown: uniform ? first : undefined,
		anyFinish: current.some(Boolean),
		sides,
		sideRoom,
	};
};
